import React, { useState, useEffect, useMemo } from 'react';
import { Button, Badge, Spinner, Alert, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import {
  Play,
  HelpCircle,
  Monitor,
  Info,
  CircleCheck,
  Workflow,
  Loader2,
  CircleAlert,
  Eye,
  Download
} from 'lucide-react';
import { FaListOl } from 'react-icons/fa6';
import { ChevronLeft, FileEarmarkPdfFill } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import { X } from 'react-bootstrap-icons';
import Accordion from '../../common/Accordion';
import { makeRESTApiRequest } from '../../../helpers/api';
import { API_URL, ENDPOINTS } from '../../../helpers/constants';
import AlertModal from '../../AlertModal';
import { encodeUrlParams } from '../../../helpers/global';

// County workflow automation UI config (must stay in sync with backend registry)
const normalizeCountyKey = county =>
  (county || '')
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const COUNTY_WORKFLOW_UI = {
  'miami dade': {
    templateName: 'Miami Dade',
    runnerKey: 'miamiDade',
    automatedSteps: new Set(['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step8', 'step9', 'step10'])
  },
  lee: {
    templateName: 'Lee',
    runnerKey: 'leeCounty',
    automatedSteps: new Set(['step1'])
  },
  broward: {
    templateName: 'Broward',
    runnerKey: 'browardCounty',
    automatedSteps: new Set(['step1'])
  },
  'palm beach': {
    templateName: 'Palm Beach',
    runnerKey: 'palmBeachCounty',
    automatedSteps: new Set(['step1'])
  }
};

const getCountyWorkflowUi = county => COUNTY_WORKFLOW_UI[normalizeCountyKey(county)] || null;

const toMessageStrings = arr => {
  if (!arr) return [];
  const list = Array.isArray(arr) ? arr : [arr];
  return list.map(m => (typeof m === 'string' ? m : String(m ?? ''))).filter(Boolean);
};

const getMessageLevel = text => {
  const trimmed = (text || '').trim();
  if (trimmed.startsWith('ERROR:')) return 'error';
  if (trimmed.startsWith('WARN:')) return 'warn';
  return 'info';
};

const MessagesWithSubgroups = ({
  messages = [],
  successMessages = [],
  failedReason = '',
  accordionTitle = 'Messages'
}) => {
  const successItems = toMessageStrings(successMessages);
  const regularItems = toMessageStrings(messages);
  const hasErrorMessage = regularItems.some(item => getMessageLevel(item) === 'error');
  const totalCount = successItems.length + regularItems.length;
  if (totalCount === 0) return null;

  const getMessagesStyle = (level, isSuccess) => {
    if (isSuccess) {
      return {
        lineStyle: 'none',
        border: '1px solid #26a65b4d',
        borderRadius: '12px',
        backgroundColor: 'transparent',
        color: '#26a65b'
      };
    }
    if (level === 'error') {
      return {
        lineStyle: 'none',
        border: '1px solid #e03e3e4d',
        borderRadius: '12px',
        backgroundColor: '#fff5f5',
        color: '#b42318'
      };
    }
    if (level === 'warn') {
      return {
        lineStyle: 'none',
        border: '1px solid #f59e0b4d',
        borderRadius: '12px',
        backgroundColor: '#fffbeb',
        color: '#b45309'
      };
    }
    return {
      lineStyle: 'none',
      border: '1px solid #e5e7eb80',
      borderRadius: '12px',
      backgroundColor: '#f8f9fa',
      color: '#737b8c'
    };
  };

  const renderItem = (text, key, isSuccess) => {
    const level = isSuccess ? 'success' : getMessageLevel(text);
    const iconClass =
      level === 'success'
        ? 'text-success'
        : level === 'error'
        ? 'text-danger'
        : level === 'warn'
        ? 'text-warning'
        : 'text-secondary';
    const textClass =
      level === 'success'
        ? 'text-success'
        : level === 'error'
        ? 'text-danger'
        : level === 'warn'
        ? 'text-warning'
        : 'text-secondary';

    return (
      <li key={key} className="px-3 py-2 mb-2 d-flex align-items-center" style={getMessagesStyle(level, isSuccess)}>
        <span
          className={`d-flex align-items-start justify-content-center flex-shrink-0 mr-2 mt-2 ${iconClass}`}
          style={{ width: 22, height: 22 }}
        >
          {isSuccess ? (
            <CircleCheck size={14} strokeWidth={2.5} />
          ) : level === 'error' ? (
            <CircleAlert size={14} strokeWidth={2.5} />
          ) : (
            <Info size={14} strokeWidth={2.5} />
          )}
        </span>
        <span className={`small ${textClass}`} style={{ lineHeight: 1.5 }}>
          {text}
        </span>
      </li>
    );
  };

  return (
    <div className="mt-2 pt-2 border-top">
      <Accordion
        title={
          <span className="smallFont steps-title-text d-flex align-items-center">
            {accordionTitle}{' '}
            {totalCount > 0 && (
              <span className="xtinyFont text-muted rounded-circle bg-light-grey px-2 py-1 ml-2 d-flex align-items-center justify-content-center">
                {totalCount}
              </span>
            )}
          </span>
        }
        triggerClassName="hover-none"
        defaultOpen={!!(failedReason || '').trim() || hasErrorMessage}
      >
        <div>
          <div className="mt-2">
            {successItems.length > 0 && (
              <div>
                <ul className="mb-0 pl-0">{successItems.map((val, idx) => renderItem(val, `s-${idx}`, true))}</ul>
              </div>
            )}
            {regularItems.length > 0 && (
              <div>
                <ul className="mb-0 pl-0">{regularItems.map((val, idx) => renderItem(val, `m-${idx}`, false))}</ul>
              </div>
            )}
          </div>
        </div>
      </Accordion>
    </div>
  );
};

const getInstructionFileDisplayName = file => {
  if (!file || typeof file !== 'object') return 'Automated Filled PDF';
  return file?.metadata?.originalName || file?.filename || 'Automated Filled PDF';
};

const normalizeProcessingInstructions = builderSteps =>
  builderSteps
    .filter(step => step.scope === 'municipality' || (step.scope === 'county' && !step.isHidden))
    .map(step => ({
      id: step._id,
      scope: step.scope,
      order: step.order,
      tab: step.tab,
      title: step.title || '',
      description: step.description || '',
      instruction: step.instruction || '',
      files: Array.isArray(step.files) ? step.files : [],
      hasCaptcha: step.hasCaptcha || false,
      stepToRun: step.stepToRun || null
    }));

const buildWorkflowDisplayItems = (workflowApiSteps, processingInstructions, instructionTab) => {
  const workflowByName = new Map(workflowApiSteps.map(step => [step.name, step]));

  let items = processingInstructions.map(instruction => {
    return {
      instruction,
      workflowStep: workflowByName.get(instruction.stepToRun || String(instruction.id)) || null
    };
  });

  if (instructionTab) {
    items = items.filter(({ instruction }) => instruction.tab === instructionTab);
  }

  return items;
};

const FilesSection = ({ files, onDownloadFile, onPreviewFile, hasInstructionAbove }) => {
  if (!files?.length) return null;

  return (
    <div className={hasInstructionAbove ? 'px-3 pt-2 pb-3' : 'p-3'}>
      <h6 className="font-weight-bold text-muted smallFont">
        ATTACHMENTS <span className="text-muted smallFont">({files.length})</span>
      </h6>
      <div className="d-flex flex-wrap align-items-start" style={{ gap: '10px' }}>
        {files.map((file, idx) => {
          const fileId = file?._id || file;
          const displayName = getInstructionFileDisplayName(file);
          return (
            <div
              key={`${fileId}-${idx}`}
              className="workflow-instruction-file-card bg-white border d-flex align-items-center px-2 py-2"
              style={{
                borderRadius: 12,
                minWidth: 220,
                maxWidth: 300,
                gap: 10,
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
              }}
              title={displayName}
            >
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 9,
                  backgroundColor: '#ffe9ea'
                }}
              >
                <FileEarmarkPdfFill size={18} className="text-danger" />
              </div>
              <div className="text-left tinyFont font-weight-bold text-truncate">{displayName}</div>
              <div className="workflow-instruction-file-card__actions ml-auto d-flex align-items-center">
                <Button
                  type="button"
                  variant="light"
                  size="sm"
                  className="p-1 rounded-circle border d-flex align-items-center justify-content-center"
                  style={{ minWidth: 28, minHeight: 28 }}
                  title="Preview"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPreviewFile(file);
                  }}
                >
                  <Eye size={14} className="text-dark" />
                </Button>
                <Button
                  type="button"
                  variant="light"
                  size="sm"
                  className="p-1 rounded-circle border d-flex align-items-center justify-content-center"
                  style={{ minWidth: 28, minHeight: 28 }}
                  title="Download"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDownloadFile(file);
                  }}
                >
                  <Download size={14} className="text-dark" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WorkflowModal = ({
  show,
  onClose,
  service,
  rowOrderId,
  orderId,
  reportMunicipality = undefined,
  handleServiceUpdate,
  renderInline = true,
  inlineHeaderStyle = 'report',
  instructionTab = null,
  handleReportUpdate
}) => {
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [runningWorkflowStep, setRunningWorkflowStep] = useState(null);
  const [showWarning, setShowWarning] = useState(null);
  const [reRunWorkflowWarningMetadata, setReRunWorkflowWarningMetadata] = useState({
    show: false,
    stepName: null,
    stepStatus: null,
    stepToRun: null
  });
  const [activeVncSessions, setActiveVncSessions] = useState({});
  const [processingInstructions, setProcessingInstructions] = useState([]);
  const resolvedOrderId = rowOrderId || orderId || null;
  const [loadingStates, setLoadingStates] = useState({
    workflow: false,
    instructions: false
  });

  // Compute overall loading state
  const loading = loadingStates.workflow || loadingStates.instructions;

  const fetchWorkflow = async ({ silent = false } = {}) => {
    if (!service?._id) return;

    const countyWorkflow = getCountyWorkflowUi(service?.county);
    if (!countyWorkflow) {
      setWorkflowSteps([]);
      return;
    }

    if (!silent) {
      setLoadingStates(prev => ({ ...prev, workflow: true }));
    }
    try {
      const queryParams = {
        serviceId: service._id,
        workflowName: countyWorkflow.templateName
      };
      const { response, error } = await makeRESTApiRequest({
        endpoint: `${ENDPOINTS.WORKFLOWS_BASE}?${encodeUrlParams(queryParams)}`,
        method: 'GET'
      });

      if (error) {
        toast.error(error);
        console.log('error', error);
        return;
      }

      setWorkflowSteps(Array.isArray(response.data?.steps) ? response.data.steps : []);
    } catch (error) {
      toast.error('Failed to fetch workflows');
    } finally {
      if (!silent) {
        setLoadingStates(prev => ({ ...prev, workflow: false }));
      }
    }
  };

  const fetchProcessingInstructions = async () => {
    setLoadingStates(prev => ({ ...prev, instructions: true }));

    if (!service?.county) {
      setProcessingInstructions([]);
      setLoadingStates(prev => ({ ...prev, instructions: false }));
      return;
    }

    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INSTRUCTION_BUILDER_STEPS({
          county: service.county,
          municipality: reportMunicipality || service?.municipality,
          showHidden: false
        }),
        method: 'GET'
      });

      if (error || !response?.data) {
        // If no instructions found, that's okay - just set empty array
        setProcessingInstructions([]);
        return;
      }

      setProcessingInstructions(normalizeProcessingInstructions(response.data.steps));
    } catch (error) {
      console.error('Error fetching processing instructions:', error);
      setProcessingInstructions([]);
    } finally {
      setLoadingStates(prev => ({ ...prev, instructions: false }));
    }
  };

  const getFileDisplayName = file => getInstructionFileDisplayName(file);

  const fetchInstructionBuilderFileBlob = async (stepRef, file) => {
    const fileId = file?._id || file;

    if (!fileId || !stepRef?.id || !resolvedOrderId) {
      return null;
    }

    const token = localStorage.getItem('user-token');
    const url = `${API_URL}${ENDPOINTS.INSTRUCTION_BUILDER_STEP_FILE_DOWNLOAD(fileId)}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : undefined
      },
      body: JSON.stringify({
        orderId: resolvedOrderId
      })
    });

    if (!response.ok) {
      return null;
    }

    return response.blob();
  };

  const handleDownloadInstructionFile = async (instruction, file) => {
    const fileId = file?._id || file;
    if (!fileId || !instruction?.id || !instruction?.scope) {
      toast.error('File is not available for download');
      return;
    }

    const blob = await fetchInstructionBuilderFileBlob({ id: instruction.id, scope: instruction.scope }, file);
    if (!blob) {
      toast.error('Failed to download instruction file');
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = getFileDisplayName(file);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handlePreviewInstructionFile = async (instruction, file) => {
    const fileId = file?._id || file;
    if (!fileId || !instruction?.id || !instruction?.scope) {
      toast.error('File is not available for preview');
      return;
    }

    const blob = await fetchInstructionBuilderFileBlob({ id: instruction.id, scope: instruction.scope }, file);
    if (!blob) {
      toast.error('Failed to preview instruction file');
      return;
    }

    const previewUrl = URL.createObjectURL(blob);
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const runWorkflow = async (workflowName, stepToRun) => {
    if (!service?._id) {
      toast.error('No service selected');
      return;
    }

    const countyWorkflow = getCountyWorkflowUi(service?.county);
    const resolvedWorkflowName = workflowName || countyWorkflow?.runnerKey;
    if (!resolvedWorkflowName) {
      toast.error('No automation workflow configured for this county');
      return;
    }

    if (!service.driveFolder) {
      toast.error('No drive folder selected');
      return;
    }

    if (!service.folioNumbers || service.folioNumbers.length === 0) {
      toast.error('No folio numbers found');
      return;
    }

    const ORDER_REQUIRED_STEPS = new Set(['step2', 'step3', 'step4', 'step5', 'step6', 'step8', 'step9', 'step10']);

    if (ORDER_REQUIRED_STEPS.has(stepToRun) && !resolvedOrderId) {
      toast.error('No order ID found');
      return;
    }

    // Lee / Broward / Palm Beach step1 also needs an order (creates/updates report municipality)
    if (
      (resolvedWorkflowName === 'leeCounty' ||
        resolvedWorkflowName === 'browardCounty' ||
        resolvedWorkflowName === 'palmBeachCounty') &&
      stepToRun === 'step1' &&
      !resolvedOrderId
    ) {
      toast.error('No order ID found');
      return;
    }

    setRunningWorkflowStep(stepToRun || null);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.WORKFLOWS_BASE,
        method: 'POST',
        requestBody: {
          serviceId: service?._id,
          orderId: resolvedOrderId,
          workflowInfo: {
            workflowName: resolvedWorkflowName,
            step: stepToRun
          }
        }
      });

      if (error) {
        toast.error(error);
        return;
      }

      if (response.result?.workflow?.service?._id) {
        const updatedMunicipality = response?.result?.report?.municipality || null;
        const updatedService = response.result.workflow?.service;
        handleServiceUpdate({
          ...service,
          propertyType: updatedService?.propertyType,
          sellerName: updatedService?.sellerName,
          legalDescription: updatedService?.legalDescription,
          ...(updatedMunicipality && { municipality: updatedMunicipality })
        });
      }

      if (response.result?.report?._id && typeof handleReportUpdate === 'function') {
        handleReportUpdate(response.result.report);
      }

      toast.success(`Workflow "${resolvedWorkflowName}" completed successfully`);
    } catch (error) {
      toast.error('Failed to start workflow');
    } finally {
      // Refresh workflows to show updated status
      await fetchWorkflow({ silent: true });
      await fetchSessions();
      setRunningWorkflowStep(null);
    }
  };

  const fetchSessions = async () => {
    if (!service?._id) return;

    try {
      const { response } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.VNC_POOL_STATUS,
        method: 'GET'
      });

      if (response?.data?.sessions) {
        const sessionMap = {};
        response.data.sessions.forEach(session => {
          // Extract step index from session.step (e.g., "step1" -> 1 or just 1)
          const stepMatch = session.step.toString().match(/\d+/);
          if (stepMatch && session.serviceId === service?._id) {
            const stepIndex = parseInt(stepMatch[0]);
            sessionMap[stepIndex] = session;
          }
        });
        setActiveVncSessions(sessionMap);
      }
    } catch (error) {
      console.error('Error fetching VNC sessions:', error);
    }
  };

  const updateWorflowStepStatus = async (workflowName, stepName, status, subStep = null) => {
    if (!service?._id) return;

    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.WORKFLOWS_BASE,
        method: 'PUT',
        requestBody: { serviceId: service._id, workflowName, stepName, status, subStep }
      });
      if (error) {
        toast.error(error);
        return;
      }
      toast.success('Workflow step status updated successfully');
      // Refetch from backend so both steps and substeps reflect updated status
      await fetchWorkflow({ silent: true });
    } catch (error) {
      toast.error('Failed to update workflow step status');
    }
  };

  const handleMonitorClick = stepIndex => {
    // Use the cached active session from polling
    const activeSession = activeVncSessions[stepIndex];

    if (activeSession && activeSession.webUrl) {
      // Use the webUrl provided by the backend
      // Replace 'localhost' with current hostname to support remote access
      const host = window.location.hostname;
      let vncUrl = activeSession.webUrl.replace('localhost', host);

      const token = localStorage.getItem('user-token');

      if (token) {
        vncUrl += `?token=${encodeURIComponent(token)}`;
      }

      console.log('🌐 Opening VNC Viewer:', vncUrl);
      window.open(vncUrl, '_blank');
    } else {
      toast.warning('VNC session not ready yet. Please wait for the step to initialize.');
    }
  };

  const handleStepStatusChange = (step, checked, parentStepName = null, stepTitleOverride = '') => {
    const isSubstep = !!parentStepName;
    const effectiveStepName = isSubstep ? parentStepName : step?.name;
    const stepTitle = (isSubstep ? step?.description : stepTitleOverride) || step?.name;
    const countyWorkflow = getCountyWorkflowUi(service?.county);
    const workflowId = countyWorkflow?.templateName || 'Miami Dade';

    if (checked) {
      setShowWarning({
        show: true,
        stepId: step?.index,
        workflowId,
        stepName: effectiveStepName,
        subStepName: isSubstep ? step.name : null,
        message: `Are you sure you want to mark "${stepTitle}" as completed?`,
        action: 'completed'
      });
    } else {
      setShowWarning({
        show: true,
        stepId: step?.index,
        workflowId,
        stepName: effectiveStepName,
        subStepName: isSubstep ? step.name : null,
        message: `Are you sure you want to mark "${stepTitle}" as incomplete?`,
        action: 'not_started'
      });
    }
  };

  useEffect(() => {
    if (show && service?._id) {
      // Reset warning state when modal opens for a new service
      setShowWarning(null);
      fetchWorkflow();
    }
  }, [show, service?._id, service?.county, resolvedOrderId]);

  useEffect(() => {
    if (show && service?._id) {
      fetchProcessingInstructions();
    }
  }, [show, service?._id, service?.county, service?.municipality, reportMunicipality, resolvedOrderId]);

  const hasServerRunningWorkflowStep = workflowSteps.some(s => s?.status === 'running');

  useEffect(() => {
    if (!show || !service?._id) return undefined;
    fetchWorkflow({ silent: true });
    const intervalId = setInterval(() => {
      fetchWorkflow({ silent: true });
    }, 4000);
    return () => clearInterval(intervalId);
  }, [show, service?._id, service?.county, resolvedOrderId]);

  // Poll for active VNC sessions - 2 seconds when steps are running
  useEffect(() => {
    const hasServerRunning = workflowSteps.some(s => s?.status === 'running');
    const hasRunningStep = !!runningWorkflowStep || hasServerRunning;

    let intervalId;
    if (show && service?._id && hasRunningStep) {
      // Start polling for VNC sessions when all conditions are true
      fetchSessions(); // Immediate fetch
      intervalId = setInterval(fetchSessions, 2000); // Poll every 2 seconds
    }

    // Cleanup interval on unmount or when conditions change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [show, service?._id, runningWorkflowStep, hasServerRunningWorkflowStep]);

  const workflowDisplayItems = useMemo(
    () => buildWorkflowDisplayItems(workflowSteps, processingInstructions, instructionTab),
    [workflowSteps, processingInstructions, instructionTab]
  );

  const workflowContent = (
    <>
      <div className={renderInline ? 'px-2 pt-4' : 'px-3 py-4'}>
        {loading ? (
          <div className="text-center pb-5">
            <Spinner animation="border" />
            <p className="mt-3 text-muted">Loading workflow configuration…</p>
          </div>
        ) : workflowDisplayItems.length === 0 ? (
          <Alert className="my-2" variant="light">
            <Alert.Heading className="h6 mb-2">No steps available</Alert.Heading>
            <p className="mb-0 text-muted">There are currently no workflow steps configured for this service.</p>
          </Alert>
        ) : (
          <div className="steps-list">
            {workflowDisplayItems.map((displayItem, displayIndex) => {
              const { instruction, workflowStep: step } = displayItem;

              const countyWorkflow = getCountyWorkflowUi(service?.county);
              const shouldShowRunButton =
                !!countyWorkflow && countyWorkflow.automatedSteps.has(instruction.stepToRun);
              const currentStatus = step?.status || 'not_started';
              const stepError = step?.failedReason || '';

              const stepTitle = instruction.title;
              const stepDescription = instruction.description || '';
              const displayDescription =
                stepDescription && stepDescription.length > 120
                  ? `${stepDescription.slice(0, 117)}...`
                  : stepDescription;
              const instructionHtml = instruction.instruction || '';
              const instructionFiles = instruction.files || [];
              const stepScope = instruction?.scope || '';
              const statusBorderColor = currentStatus === 'failed' ? '#e03e3e4d' : '#e5e7ebcc';
              const scopeBorderColor = stepScope === 'county' ? 'var(--info)' : 'var(--success)';

              return (
                <div
                  key={`${instruction.scope}-${instruction.id}`}
                  className="mb-3 bg-white hover-shadow d-flex"
                  style={{
                    borderWidth: '1px 1px 1px 4px',
                    borderStyle: 'solid',
                    borderColor: `${statusBorderColor} ${statusBorderColor} ${statusBorderColor} ${scopeBorderColor}`,
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div>
                    <Form.Check
                      type="checkbox"
                      checked={currentStatus === 'completed'}
                      onChange={e => {
                        e.preventDefault();
                        const checked = e.target.checked;
                        const stepForHandler = step || {
                          name: instruction.stepToRun || String(instruction.id),
                          index: displayIndex + 1
                        };
                        handleStepStatusChange(stepForHandler, checked, null, stepTitle);
                      }}
                      disabled={
                        currentStatus === 'running' || runningWorkflowStep === step?.index || showWarning !== null
                      }
                      className={`custom-step-checkbox 
                                  ${currentStatus === 'failed' ? 'text-danger' : ''}
                                  ${currentStatus === 'completed' ? 'text-success' : ''}
                                `}
                    />
                  </div>
                  <div className="flex-grow-1 ml-3" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center justify-content-between" style={{ minWidth: 0 }}>
                      <div
                        className="d-flex align-items-center justify-content-between flex-grow-1"
                        style={{ minWidth: 0 }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                            <span
                              className="text-capitalize h6 mb-0 largeFont font-weight-semibold text-truncate d-inline-block"
                              style={{ maxWidth: '100%' }}
                            >
                              {stepTitle ? `Step ${displayIndex + 1}: ${stepTitle}` : `Step ${displayIndex + 1}`}
                            </span>
                            {(stepDescription || stepTitle) && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-${instruction.scope}-${instruction.id}`}>
                                    {stepDescription || stepTitle || ''}
                                  </Tooltip>
                                }
                              >
                                <HelpCircle size={14} className="mx-1 text-muted" />
                              </OverlayTrigger>
                            )}
                          </div>
                          {displayDescription && (
                            <div className="text-muted small mt-1 text-truncate" style={{ maxWidth: '100%' }}>
                              {displayDescription}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-items-center flex-shrink-0 ml-3">
                        {step && activeVncSessions[step.index] && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="mr-2"
                            onClick={() => handleMonitorClick(step.index)}
                            title="Click to open VNC viewer"
                          >
                            <Monitor size={14} />
                          </Button>
                        )}
                        {shouldShowRunButton && (
                          <button
                            className="d-inline-flex align-items-center font-weight-medium smallFont flex-shrink-0 px-3 py-2 hover-success-light bg-white"
                            style={{
                              border: '1px solid rgba(25, 135, 84, 0.3)',
                              borderRadius: '10px',
                              color: 'var(--success)',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => {
                              if (currentStatus === 'completed') {
                                setReRunWorkflowWarningMetadata({
                                  show: true,
                                  stepName: step?.name,
                                  stepStatus: currentStatus,
                                  stepToRun: instruction.stepToRun
                                });
                              } else {
                                runWorkflow(countyWorkflow?.runnerKey, instruction.stepToRun);
                              }
                            }}
                            disabled={runningWorkflowStep === instruction.stepToRun || currentStatus === 'running'}
                          >
                            {runningWorkflowStep === instruction.stepToRun || currentStatus === 'running' ? (
                              <>
                                <Loader2
                                  size={16}
                                  className="mr-1 spinning"
                                  style={{ animation: 'spinner-border .75s linear infinite' }}
                                />
                                {renderInline ? null : 'Running...'}
                              </>
                            ) : (
                              <>
                                <Play size={16} className="mr-1" fill="var(--success)" />
                                {renderInline ? null : currentStatus === 'completed' ? 'Re-run' : 'Run'}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {instruction.hasCaptcha && (
                      <div className="mt-1">
                        <Badge variant="warning" className="small">
                          Captcha Solver Required
                        </Badge>
                      </div>
                    )}

                    {(() => {
                      const hasInstructionBlock = !!instructionHtml || instructionFiles.length > 0;
                      if (!hasInstructionBlock) return null;

                      return (
                        <div className="border-top mt-2">
                          <Accordion
                            title={
                              <span className="smallFont steps-title-text d-flex align-items-center">
                                Manual instructions
                              </span>
                            }
                            triggerClassName="hover-none py-2"
                            defaultOpen={false}
                          >
                            <div className="mb-2 p-3 border rounded bg-light instruction-content">
                              {instructionHtml && (
                                <div
                                  dangerouslySetInnerHTML={{ __html: instructionHtml }}
                                  style={{
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    color: '#374151'
                                  }}
                                />
                              )}
                              <FilesSection
                                files={instructionFiles}
                                hasInstructionAbove={!!instructionHtml}
                                onDownloadFile={file => handleDownloadInstructionFile(instruction, file)}
                                onPreviewFile={file => handlePreviewInstructionFile(instruction, file)}
                              />
                            </div>
                          </Accordion>
                        </div>
                      );
                    })()}

                    {currentStatus === 'failed' && (
                      <Alert variant="danger" className="py-2 mt-2 mb-0">
                        <span className="d-flex align-items-center smallFont py-1">
                          <CircleAlert size={16} className="mr-2 text-danger flex-shrink-0" />
                          <span>Error: {stepError || 'This step failed to execute.'}</span>
                        </span>
                      </Alert>
                    )}

                    {
                      <MessagesWithSubgroups
                        messages={step?.messages}
                        successMessages={step?.successMessages}
                        failedReason={currentStatus === 'failed' ? stepError : ''}
                        accordionTitle="Messages"
                      />
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <AlertModal
          show={showWarning?.show}
          onHide={() => setShowWarning(null)}
          onContinueClick={() => {
            updateWorflowStepStatus(
              showWarning.workflowId,
              showWarning.stepName,
              showWarning.action,
              showWarning.subStepName || null
            );
            setShowWarning(null);
          }}
          onDismissClick={() => setShowWarning(null)}
          alertText={showWarning?.message}
          progressText="Updating status..."
        />

        <AlertModal
          show={reRunWorkflowWarningMetadata?.show}
          onHide={() =>
            setReRunWorkflowWarningMetadata({ show: false, stepName: null, stepStatus: null, stepToRun: null })
          }
          onContinueClick={() => {
            if (reRunWorkflowWarningMetadata.stepToRun) {
              const countyWorkflow = getCountyWorkflowUi(service?.county);
              runWorkflow(countyWorkflow?.runnerKey, reRunWorkflowWarningMetadata.stepToRun);
            }
            setReRunWorkflowWarningMetadata({ show: false, stepName: null, stepStatus: null, stepToRun: null });
          }}
          onDismissClick={() =>
            setReRunWorkflowWarningMetadata({ show: false, stepName: null, stepStatus: null, stepToRun: null })
          }
          alertText={`Are you sure you want to re-run the workflow?`}
          progressText="Re-running workflow..."
        />
      </div>
    </>
  );

  const useReportInlineHeader = renderInline && inlineHeaderStyle === 'report';

  const workflowHeader = (
    <div className="d-flex align-items-center px-3 py-2">
      {useReportInlineHeader ? (
        <Button
          type="button"
          variant="success"
          className="d-inline-flex align-items-center px-2 py-2 mr-3"
          style={{ borderRadius: '10px', gap: '8px' }}
          onClick={onClose}
        >
          <FaListOl size={16} />
          <ChevronLeft size={14} />
        </Button>
      ) : (
        <div
          className="d-flex align-items-center justify-content-center p-2"
          style={{ background: '#26a65b1a', borderRadius: '12px', marginRight: '12px' }}
        >
          <Workflow size={20} className="text-success" />
        </div>
      )}

      <div className="flex-grow-1">
        <h1 className="xlargeFont fw-bold mb-0 text-nowrap">Processing Workflow</h1>
      </div>

      {renderInline && inlineHeaderStyle === 'sidebar' && (
        <Button size="sm" className="close_button h-100" onClick={onClose} variant="danger">
          Close <X size={20} className="align-text-top" />
        </Button>
      )}
    </div>
  );

  if (!show) {
    return null;
  }

  if (renderInline) {
    return (
      <div
        className="p-2"
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          animation: 'workflow-panel-slide-in 260ms ease-out',
          willChange: 'transform, opacity'
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            flexShrink: 0,
            backgroundColor: '#fafafa'
          }}
        >
          {workflowHeader}
          <hr className="my-2 mb-0" />
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
          className="scrollbar-thin"
        >
          {workflowContent}
        </div>
      </div>
    );
  }

  return workflowContent;
};

export default WorkflowModal;
